import { StyledButton, StyledLoadingButton } from '../../../../../common/Styleguide/Common/Button';
import { useContext, useState } from 'react';
import { GraphQLError } from '../../../../../common/components/ApolloError/GraphQLError';
import SessionContext from '../../../../../common/Session/SessionContext';
import {
  _testUtilityCreatePartialDraftSubmission,
  _testUtilityCreateFullDraftSubmission,
  _testUtilityCreateSubmittedSubmission,
  _testUtilityCreateLeaderboardData,
} from './utils';
import { ApolloError } from '@apollo/client';
import { StyledInput } from '../../../../../common/Styleguide/Common/Input';
import { Replay } from '@mui/icons-material';
import { StyledBodyText } from '../../../../../common/Styleguide/Common/Text';
import { COLORS } from '../../../../../common/Styleguide/Common/colors';
import { generateFakeLeaderboardDataForGame } from '../../../../../common/graphql/games/game-generate-fake-leaderboard-data-graphql';
import { Typography } from '@mui/material';
import Notification from '../../../../../common/Styleguide/Common/Notification';

export const ENABLE_TEST_UTILITIES = (() => {
  const host = window.location.host;

  if (host === 'localhost:3001' || host === 'developer.dev-crazygames.be') {
    return true;
  }

  return false;
})();

const LocalUtilities: React.FC = () => {
  const [error, setError] = useState<any | undefined>(undefined);
  const [creatingDraftSubmission, setCreatingDraftSubmission] = useState(false);
  const [creatingFullNotSubmitted, setCreatingFullNotSubmitted] = useState(false);
  const [creatingSubmittedSubmission, setCreatingSubmittedSubmission] = useState(false);
  const [creatingLeaderboardData, setCreatingLeaderboardData] = useState(false);
  const [generatingFakeDataForGame, setGeneratingFakeDataForGame] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [name, setName] = useState<string>(new Date().toLocaleString(navigator.language));
  const [gameId, setGameId] = useState<string>('');
  const session = useContext(SessionContext);

  const createDraftSubmission = async () => {
    setError(undefined);
    setCreatingDraftSubmission(true);
    try {
      const submission = await _testUtilityCreatePartialDraftSubmission(name, session);
      setLog([...log, `[Partial draft]   ${submission.id}   ${submission.slug}`]);
    } catch (e) {
      setError(e);
    } finally {
      setCreatingDraftSubmission(false);
    }
  };

  const createFullNotSubmittedSubmission = async () => {
    setError(undefined);
    setCreatingFullNotSubmitted(true);
    try {
      const submission = await _testUtilityCreateFullDraftSubmission(name, session);
      setLog([...log, `[Full draft]   ${submission.id}   ${submission.slug}`]);
    } catch (e) {
      setError(e);
    } finally {
      setCreatingFullNotSubmitted(false);
    }
  };

  const createSubmittedSubmission = async () => {
    setError(undefined);
    setCreatingSubmittedSubmission(true);
    try {
      const submission = await _testUtilityCreateSubmittedSubmission(name, session);
      setLog([...log, `[Submitted]   ${submission.id}   ${submission.slug}`]);
    } catch (e) {
      setError(e);
    } finally {
      setCreatingSubmittedSubmission(false);
    }
  };

  const createLeaderboardData = async () => {
    setError(undefined);
    setCreatingLeaderboardData(true);
    try {
      const result = await _testUtilityCreateLeaderboardData(name, session);
      setLog([...log, `[Leaderboard]   ${result.gameId}   ${result.slug}   (submission: ${result.submissionId})`]);
    } catch (e) {
      setError(e);
    } finally {
      setCreatingLeaderboardData(false);
    }
  };

  const generateFakeDataForGame = async () => {
    setError(undefined);
    setGeneratingFakeDataForGame(true);
    try {
      const response = await generateFakeLeaderboardDataForGame({ gameId });
      if (response.data?.adminGameGenerateFakeLeaderboardData.success) {
        setLog([...log, `[Fake Data for Game]   Game ID: ${gameId}   Successfully generated fake leaderboard data`]);
      } else {
        setError(new Error('Failed to generate fake leaderboard data'));
      }
    } catch (e) {
      setError(e);
    } finally {
      setGeneratingFakeDataForGame(false);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: 700 }}>
        <Notification
          theme="warning"
          description={
            <Typography variant="body2">
              <strong>Test Data Utilities</strong>
              <br />
              These utilities are for testing purposes only. They create fake users, games, and leaderboard data.
            </Typography>
          }
        />
        <h2>Create submission</h2>
        <div>
          Name
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <StyledInput
              sx={{ flex: 1 }}
              value={name}
              onChange={(e) => {
                setName(e.currentTarget.value);
              }}
              onBlur={(e) => {
                setName(e.currentTarget.value);
              }}
            />
            <StyledButton
              onClick={() => {
                setName(new Date().toLocaleString(navigator.language));
              }}
            >
              <Replay />
            </StyledButton>
          </div>
          <StyledBodyText variant="bodyLower" sx={{ m: 0.5, color: COLORS.white[30] }}>
            Feel free to type other name also.
          </StyledBodyText>
        </div>
        <div>
          <StyledLoadingButton loading={creatingDraftSubmission} onClick={createDraftSubmission} variant="contained" sx={{ width: 300 }}>
            Partial Draft
          </StyledLoadingButton>
          <StyledBodyText variant="bodyLower" sx={{ m: 0.5, color: COLORS.white[30] }}>
            Submission with first step completed (name, engine, files, etc). No QA tool passed yet.
          </StyledBodyText>
        </div>
        <div>
          <StyledLoadingButton
            loading={creatingFullNotSubmitted}
            onClick={createFullNotSubmittedSubmission}
            variant="contained"
            sx={{ width: 300 }}
          >
            Full Draft
          </StyledLoadingButton>
          <StyledBodyText variant="bodyLower" sx={{ m: 0.5, color: COLORS.white[30] }}>
            Passed QA, has covers, description, name, etc. Needs legal requirements to be completed.
          </StyledBodyText>
        </div>
        <div>
          <StyledLoadingButton
            loading={creatingSubmittedSubmission}
            onClick={createSubmittedSubmission}
            variant="contained"
            sx={{ width: 300 }}
          >
            Submitted
          </StyledLoadingButton>
          <StyledBodyText variant="bodyLower" sx={{ m: 0.5, color: COLORS.white[30] }}>
            Passed QA, has covers, description, name, legal form completed and submitted, needs QA review now.
          </StyledBodyText>
        </div>
        <h2 style={{ marginTop: 32 }}>Generate new game with fake leaderboard data</h2>
        <Notification
          theme="warning"
          description={
            <Typography variant="body2">
              The game itself will <strong>not</strong> be playable right away. You need to:
              <br />- update covers and video
              <br />- synchronize them from admin page
              <br />- [optional] create a new build with your game files.
            </Typography>
          }
        />
        <div>
          <StyledLoadingButton loading={creatingLeaderboardData} onClick={createLeaderboardData} variant="contained" sx={{ width: 300 }}>
            Generate Fake Data
          </StyledLoadingButton>
          <StyledBodyText variant="bodyLower" sx={{ m: 0.5, color: COLORS.white[30] }}>
            Creates a released submission with fake leaderboard set up with test users and scores.
          </StyledBodyText>
        </div>
        <h2 style={{ marginTop: 32 }}>Generate fake leaderboard scores for existing game</h2>
        <div>
          Game ID
          <StyledInput
            sx={{ width: '100%' }}
            value={gameId}
            onChange={(e) => {
              setGameId(e.currentTarget.value);
            }}
            placeholder="Enter game ID"
          />
          <StyledBodyText variant="bodyLower" sx={{ m: 0.5, color: COLORS.white[30] }}>
            Enter game ID
          </StyledBodyText>
        </div>
        <div>
          <StyledLoadingButton
            loading={generatingFakeDataForGame}
            onClick={generateFakeDataForGame}
            variant="contained"
            sx={{ width: 300 }}
            disabled={!gameId}
          >
            Generate More Fake Data
          </StyledLoadingButton>
          <StyledBodyText variant="bodyLower" sx={{ m: 0.5, color: COLORS.white[30] }}>
            Generates more fake leaderboard data (both users and scores) for an existing game
          </StyledBodyText>
        </div>
      </div>
      {error && error instanceof ApolloError && <GraphQLError error={error} />}
      {error && !(error instanceof ApolloError) && <div>{error.toString()}</div>}
      {log.length > 0 && <h2>Log</h2>}
      <div style={{ marginTop: 24 }}>
        {log.map((l) => (
          <div key={l}>{l}</div>
        ))}
      </div>
    </>
  );
};

export default LocalUtilities;
